import type { ReactNode } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { api, ApiError, post } from './api'
import type { Me } from './api'

interface Session {
  /** undefined = loading, null = signed out. */
  me: Me | null | undefined
  refresh: () => Promise<void>
  signOut: () => Promise<void>
}

const SessionContext = createContext<Session | null>(null)

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null | undefined>(undefined)
  const refresh = useCallback(async () => {
    try {
      setMe(await api<Me>('/me'))
    } catch (e) {
      if (e instanceof ApiError && e.status === 401) setMe(null)
      else setMe(null)
    }
  }, [])
  const signOut = useCallback(async () => {
    await post('/auth/logout', {}).catch(() => undefined)
    setMe(null)
  }, [])
  useEffect(() => { void refresh() }, [refresh])
  const value = useMemo(() => ({ me, refresh, signOut }), [me, refresh, signOut])
  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>
}

export function useSession(): Session {
  const ctx = useContext(SessionContext)
  if (!ctx) throw new Error('useSession must be used inside <SessionProvider>')
  return ctx
}
